import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Calendar, CheckCircle2, ChevronDown, Clock3, FilePlus2, LogOut, Plus, Save, Star, Trash2, Users, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import CommunityBoxes from '../components/CommunityBoxes';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
function buildDefaultWeek() {
  return Object.fromEntries(DAYS.map((day) => [day, { subjects: [] }]));
}

function normalizeWeek(week) {
  const normalized = buildDefaultWeek();
  if (!week || typeof week !== 'object') {
    return normalized;
  }

  DAYS.forEach((day) => {
    const subjects = Array.isArray(week?.[day]?.subjects) ? week[day].subjects : [];
    normalized[day] = {
      subjects: subjects
        .map((subject) => String(subject || '').trim())
        .filter(Boolean),
    };
  });

  return normalized;
}

function weekHasSubjects(week) {
  return DAYS.some((day) => (week?.[day]?.subjects || []).length > 0);
}

function weekSignature(week) {
  return JSON.stringify(normalizeWeek(week));
}

function getProgressEntry(progress, day, subject) {
  const entry = progress?.[day]?.[subject];
  if (entry && typeof entry === 'object') {
    return {
      completed: Boolean(entry.completed),
      questions_solved: entry.questions_solved || '',
    };
  }
  return {
    completed: Boolean(entry),
    questions_solved: '',
  };
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('tr-TR');
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR');
}

function packageStatus(student) {
  if (!student?.package_type) return 'Paket yok';
  return student.package_active ? 'Aktif' : 'Pasif';
}

export default function MentorDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [schedule, setSchedule] = useState(buildDefaultWeek());
  const [persistedSchedule, setPersistedSchedule] = useState(buildDefaultWeek());
  const [progress, setProgress] = useState({});
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [openHistoryItems, setOpenHistoryItems] = useState({});
  const [deletingHistoryKey, setDeletingHistoryKey] = useState('');
  const [currentScheduleCreatedAt, setCurrentScheduleCreatedAt] = useState('');
  const [libraryResources, setLibraryResources] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({ average: 0, total: 0 });
  const [siteSettings, setSiteSettings] = useState({});

  const unreadNotificationCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const fetchStudents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mentor/students`, { withCredentials: true });
      setStudents(data);
    } catch (error) {
      console.error(error);
      toast.error('Ogrenciler yuklenemedi.');
    }
  }, []);

  const fetchMyRatings = useCallback(async () => {
    if (!user?._id) return;
    try {
      const { data } = await axios.get(`${API}/mentor/${user._id}/ratings`);
      setRatingSummary({ average: data.average || 0, total: data.total || 0 });
    } catch (error) {
      console.error('Mentor puan ozeti alinamadi:', error);
    }
  }, [user?._id]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mentor/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (error) {
      console.error('Mentor bildirimleri alinamadi:', error);
    }
  }, []);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      setSiteSettings(data || {});
    } catch (error) {
      console.error('Site ayarlari alinamadi:', error);
    }
  }, []);

  const loadMentorDashboard = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStudents(), fetchMyRatings(), fetchNotifications(), fetchSiteSettings()]);
    } finally {
      setLoading(false);
    }
  }, [fetchMyRatings, fetchNotifications, fetchSiteSettings, fetchStudents]);

  useEffect(() => {
    loadMentorDashboard();
  }, [loadMentorDashboard]);

  const applyScheduleResponse = useCallback((data) => {
    const nextWeek = normalizeWeek(data.week_data || buildDefaultWeek());
    setSchedule(nextWeek);
    setPersistedSchedule(nextWeek);
    setProgress(data.progress || {});
    setScheduleHistory(Array.isArray(data.schedule_history) ? data.schedule_history : []);
    setOpenHistoryItems({});
    setCurrentScheduleCreatedAt(data.current_schedule_created_at || '');
    setLibraryResources(Array.isArray(data.library_resources) ? data.library_resources : []);
  }, []);

  const toggleHistoryItem = (historyKey) => {
    setOpenHistoryItems((prev) => ({
      ...prev,
      [historyKey]: !prev[historyKey],
    }));
  };

  const refreshScheduleEditor = useCallback(
    async (studentId) => {
      const { data } = await axios.get(`${API}/mentor/schedule/${studentId}`, { withCredentials: true });
      applyScheduleResponse(data);
    },
    [applyScheduleResponse]
  );

  const openScheduleEditor = async (student) => {
    try {
      const { data } = await axios.get(`${API}/mentor/schedule/${student._id}`, { withCredentials: true });
      applyScheduleResponse(data);
      setSelectedStudent(student);
      setEditOpen(true);
    } catch (error) {
      toast.error('Cizelge yuklenemedi.');
    }
  };

  const updateCurrentSchedule = async () => {
    if (!selectedStudent) return;
    try {
      setSavingSchedule(true);
      await axios.put(
        `${API}/mentor/schedule/${selectedStudent._id}`,
        { week_data: schedule, mode: 'update' },
        { withCredentials: true }
      );
      await refreshScheduleEditor(selectedStudent._id);
      await fetchStudents();
      toast.success('Mevcut cizelge guncellendi.');
    } catch (error) {
      toast.error('Cizelge guncellenemedi.');
    } finally {
      setSavingSchedule(false);
    }
  };

  const createNewSchedule = async () => {
    if (!selectedStudent) return;
    const currentPersistedWeek = normalizeWeek(persistedSchedule);
    const currentEditorWeek = normalizeWeek(schedule);
    const hasPersistedActiveSchedule = weekHasSubjects(currentPersistedWeek);
    const hasUnsavedChanges = weekSignature(currentPersistedWeek) !== weekSignature(currentEditorWeek);

    if (!hasPersistedActiveSchedule) {
      if (weekHasSubjects(currentEditorWeek)) {
        toast.error('Bu cizelge henuz aktif olarak kaydedilmemis. Once Tamamini Guncelle ile kaydet.');
      } else {
        toast.error('Gecmise tasinacak aktif bir cizelge bulunmuyor.');
      }
      return;
    }

    if (hasUnsavedChanges) {
      const continueConfirmed = window.confirm(
        'Kaydedilmemis degisiklikler var. Yeni cizelge olusturursan sadece son kaydedilen aktif cizelge gecmise tasinir. Devam edilsin mi?'
      );
      if (!continueConfirmed) return;
    }

    const confirmed = window.confirm('Yeni cizelge olusturuldugunda mevcut aktif cizelge gecmise tasinacak. Devam edilsin mi?');
    if (!confirmed) return;

    try {
      setCreatingSchedule(true);
      await axios.put(
        `${API}/mentor/schedule/${selectedStudent._id}`,
        { week_data: buildDefaultWeek(), archive_week_data: currentPersistedWeek, mode: 'new' },
        { withCredentials: true }
      );
      await refreshScheduleEditor(selectedStudent._id);
      await fetchStudents();
      toast.success('Yeni cizelge olusturuldu. Onceki cizelge gecmise tasindi.');
    } catch (error) {
      toast.error('Yeni cizelge olusturulamadi.');
    } finally {
      setCreatingSchedule(false);
    }
  };

  const deleteHistoryItem = async (historyKey) => {
    if (!selectedStudent) return;
    const confirmed = window.confirm('Bu gecmis cizelge kaydini silmek istedigine emin misin?');
    if (!confirmed) return;

    try {
      setDeletingHistoryKey(historyKey);
      await axios.delete(`${API}/mentor/schedule/${selectedStudent._id}/history/${historyKey}`, { withCredentials: true });
      await refreshScheduleEditor(selectedStudent._id);
      toast.success('Gecmis cizelge silindi.');
    } catch (error) {
      toast.error('Gecmis cizelge silinemedi.');
    } finally {
      setDeletingHistoryKey('');
    }
  };

  const addSubject = (day) => {
    const subject = window.prompt(`${day} icin yeni konu/aktivite gir:`);
    if (!subject?.trim()) return;
    setSchedule((prev) => ({
      ...prev,
      [day]: { subjects: [...(prev?.[day]?.subjects || []), subject.trim()] },
    }));
  };

  const removeSubject = (day, index) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { subjects: prev?.[day]?.subjects?.filter((_, subjectIndex) => subjectIndex !== index) || [] },
    }));
  };

  const handleNotificationDialogChange = async (open) => {
    setNotifOpen(open);
    if (open && unreadNotificationCount > 0) {
      try {
        await axios.put(`${API}/mentor/notifications/read-all`, {}, { withCredentials: true });
        setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      } catch (error) {
        console.error('Mentor bildirim sifirlama hatasi:', error);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="bg-black/60 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center pl-1 hover:opacity-90 transition-opacity">
            <span className="text-[1.7rem] font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              KARGA AKADEMI
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => handleNotificationDialogChange(true)}
              variant="outline"
              className="border-white/10 text-white hover:border-[#B026FF]/50 hover:text-[#B026FF] relative"
            >
              <Bell className="w-4 h-4" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadNotificationCount}
                </span>
              )}
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 text-white hover:text-[#B026FF]">
              <LogOut className="w-4 h-4 mr-2" /> Cikis
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12" data-testid="mentor-dashboard">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-7 h-7 text-[#B026FF]" />
              <div>
                <p className="text-sm text-[#A1A1AA]">Ogrenci Sayin</p>
                <h2 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {students.length}
                </h2>
              </div>
            </div>
            <p className="text-sm text-[#71717A]">Atanmis ogrencilerini ve cizelgelerini bu panelden yonetebilirsin.</p>
          </div>

          <div className="bg-[#0A0A0A] border border-yellow-500/30 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <Star className="w-7 h-7 text-yellow-400" />
              <div>
                <p className="text-sm text-[#A1A1AA]">Alinan Ortalama Puaniniz</p>
                <h2 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {ratingSummary.average}/5
                </h2>
              </div>
            </div>
            <p className="text-sm text-[#71717A]">{ratingSummary.total} degerlendirme uzerinden hesaplandi.</p>
          </div>

          <div className="bg-[#0A0A0A] border border-green-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="w-7 h-7 text-green-400" />
              <div>
                <p className="text-sm text-[#A1A1AA]">Tamamlanan Gorev Bildirimleri</p>
                <h2 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {notifications.filter((notification) => notification.type === 'student_progress').length}
                </h2>
              </div>
            </div>
            <p className="text-sm text-[#71717A]">Ogrencilerin isaretledigi gorevleri bildirimlerden takip edebilirsin.</p>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-8 h-8 text-[#B026FF]" />
            <h2 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ogrencilerim
            </h2>
          </div>
          <p className="text-[#A1A1AA]">Paket bilgilerini, baslangic-bitis tarihlerini ve cizelge durumlarini burada gor.</p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#B026FF]">Yukleniyor...</div>
        ) : students.length === 0 ? (
          <div className="text-center py-20 text-[#71717A]">Henuz atanmis ogrencin yok.</div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-x-auto">
            <table className="w-full" data-testid="mentor-students-table">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">Ad</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">E-posta</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">Paket</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">Baslangic</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">Bitis</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#E0E0E0]">Durum</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#E0E0E0]">Islem</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student._id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-6 py-4 text-sm">{student.name}</td>
                    <td className="px-6 py-4 text-sm text-[#A1A1AA]">{student.email}</td>
                    <td className="px-6 py-4 text-sm text-[#A1A1AA]">{student.package_type || '-'}</td>
                    <td className="px-6 py-4 text-sm text-[#A1A1AA]">{formatDate(student.package_start)}</td>
                    <td className="px-6 py-4 text-sm text-[#A1A1AA]">{formatDate(student.package_end)}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={student.package_active ? 'text-green-400' : 'text-yellow-400'}>
                        {packageStatus(student)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button onClick={() => openScheduleEditor(student)} size="sm" className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                        <Calendar className="w-4 h-4 mr-2" /> Cizelge
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-10">
          <CommunityBoxes boxes={siteSettings.community_boxes} title="Topluluk" />
        </div>
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedStudent?.name} - Cizelge
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Tamamlanan gorevler yesil ve uzeri cizili gorunur. Yeni cizelge olusturursan mevcut aktif cizelge otomatik olarak gecmise tasinir.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Yeni Cizelge Olustur
                </h4>
                <p className="text-sm text-[#A1A1AA]">
                  Aktif cizelge olusturma tarihi: {formatDateTime(currentScheduleCreatedAt)}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={createNewSchedule}
                  disabled={creatingSchedule}
                  variant="outline"
                  className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10"
                >
                  <FilePlus2 className="w-4 h-4 mr-2" />
                  {creatingSchedule ? 'Olusturuluyor...' : 'Yeni Cizelge Olustur'}
                </Button>
                <Button
                  onClick={updateCurrentSchedule}
                  disabled={savingSchedule}
                  className="bg-[#B026FF] hover:bg-[#C966FF] text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingSchedule ? 'Guncelleniyor...' : 'Tamamini Guncelle'}
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {DAYS.map((day) => (
              <div key={day} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {day}
                  </h4>
                  <Button
                    onClick={() => addSubject(day)}
                    size="sm"
                    variant="outline"
                    className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {(schedule?.[day]?.subjects || []).map((subject, index) => {
                    const progressEntry = getProgressEntry(progress, day, subject);
                    const completed = progressEntry.completed;
                    return (
                      <div
                        key={`${day}-${index}-${subject}`}
                        className={`flex items-center justify-between rounded px-3 py-2 border ${
                          completed
                            ? 'bg-green-500/10 border-green-500/30 text-green-100'
                            : 'bg-black/40 border-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {completed && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                          <div>
                            <span className={`text-sm ${completed ? 'line-through' : 'text-[#E0E0E0]'}`}>{subject}</span>
                            {progressEntry.questions_solved && (
                              <p className="text-[11px] text-[#A1A1AA] mt-1">Gunluk cozulen soru: {progressEntry.questions_solved}</p>
                            )}
                          </div>
                        </div>
                        <button onClick={() => removeSubject(day, index)} className="text-red-400 hover:text-red-300">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}

                  {!(schedule?.[day]?.subjects?.length) && (
                    <p className="text-[#71717A] text-sm italic">Konu eklenmedi.</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <h4 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kullanici Kutuphanesi / Kaynaklari
            </h4>
            {libraryResources.length === 0 ? (
              <p className="text-sm text-[#71717A] italic">Ogrenci henuz kaynak eklemedi.</p>
            ) : (
              <div className="space-y-3">
                {libraryResources.map((item) => (
                  <div key={item._id} className="rounded-lg border border-white/10 bg-black/30 p-3">
                    <p className="text-sm font-semibold text-white">{item.title || 'Adsiz Kaynak'}</p>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block break-all text-xs text-[#60A5FA] hover:text-[#93C5FD]"
                      >
                        {item.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Clock3 className="w-5 h-5 text-[#B026FF]" />
              <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Gecmis Cizelgeyi Goruntule
              </h4>
            </div>
            {scheduleHistory.length === 0 ? (
              <p className="text-sm text-[#71717A] italic">Bu ogrenci icin henuz gecmis cizelge bulunmuyor.</p>
            ) : (
              <div className="space-y-4">
                {scheduleHistory.map((historyItem, historyIndex) => (
                  (() => {
                    const historyKey = historyItem._id || `${historyItem.schedule_version}-${historyIndex}`;
                    const isOpen = Boolean(openHistoryItems[historyKey]);
                    return (
                      <div key={historyKey} className="rounded-lg border border-white/10 bg-black/30">
                        <div className="flex items-center gap-3 px-4 py-4">
                          <button
                            type="button"
                            onClick={() => toggleHistoryItem(historyKey)}
                            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                          >
                            <div>
                              <p className="text-sm font-semibold text-white">
                                Gecmis Cizelge #{historyItem.schedule_version || historyIndex + 1}
                              </p>
                              <p className="text-xs text-[#A1A1AA]">
                                Olusturma: {formatDateTime(historyItem.created_at)} | Gecmise dusme: {formatDateTime(historyItem.archived_at)}
                              </p>
                            </div>
                            <ChevronDown className={`h-4 w-4 shrink-0 text-[#A1A1AA] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => deleteHistoryItem(historyKey)}
                            disabled={deletingHistoryKey === historyKey}
                            className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {deletingHistoryKey === historyKey ? 'Siliniyor...' : 'Sil'}
                          </Button>
                        </div>
                        {isOpen && (
                          <div className="border-t border-white/10 px-4 pb-4 pt-4">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              {DAYS.map((day) => {
                                const dayData = historyItem.week_data?.[day] || { subjects: [] };
                                return (
                                  <div key={`${historyKey}-${day}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                                    <h5 className="mb-2 font-semibold text-white">{day}</h5>
                                    {dayData.subjects?.length > 0 ? (
                                      <div className="space-y-2">
                                        {dayData.subjects.map((subject, subjectIndex) => (
                                          <div key={`${historyKey}-${day}-${subjectIndex}`} className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-[#D4D4D8]">
                                            {subject}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-sm italic text-[#71717A]">Konu eklenmedi.</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setEditOpen(false)} variant="outline" className="border-white/10 text-white">
              Kapat
            </Button>
            <Button onClick={updateCurrentSchedule} disabled={savingSchedule} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              {savingSchedule ? 'Guncelleniyor...' : 'Tamamini Guncelle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notifOpen} onOpenChange={handleNotificationDialogChange}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mentor Bildirimleri
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Ogrenci gorev tamamlamalari ve sana verilen puanlamalar burada gorunur.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[420px] overflow-y-auto py-2">
            {notifications.length === 0 ? (
              <p className="text-[#71717A] text-center py-4">Bildirim yok.</p>
            ) : (
              notifications.map((notification, index) => (
                <div
                  key={`${notification.notification_key || notification.created_at || index}`}
                  className={`border rounded-lg p-3 ${
                    notification.read ? 'border-white/5 opacity-70' : 'border-[#B026FF]/30 bg-[#B026FF]/5'
                  }`}
                >
                  <p className="text-sm text-[#E0E0E0]">{notification.message}</p>
                  <span className="text-xs text-[#71717A]">
                    {notification.created_at ? new Date(notification.created_at).toLocaleString('tr-TR') : '-'}
                  </span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
